import React, { useEffect, useRef, useCallback, useMemo, memo } from 'react'
import styled from 'styled-components'
import DOMPurify from 'dompurify'
import {
  ProductDetailBaseInfo,
  ProductDetailType,
  ProductDutyInfo,
  ProductDutyInfoContents,
} from '../../@types/product'

interface ProductDetailContentsProps {
  product: ProductDetailType
  productItem?: ProductDetailBaseInfo
  productInfo?: ProductDutyInfo
  keyArray: string[]
  valueArray: string[]
}

const ProductDetailContents = ({
  product,
  productItem,
  productInfo,
  keyArray,
  valueArray,
}: ProductDetailContentsProps) => {
  const tabBtnsRef = useRef<HTMLUListElement>(null)

  const sanitizedContent = useMemo(() => {
    const config = {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'],
    }
    return DOMPurify.sanitize(productItem?.content || '', config)
  }, [productItem?.content])
  const sanitizedDeliveryGuide = useMemo(() => DOMPurify.sanitize(product.deliveryGuide), [product.deliveryGuide])
  const sanitizedRefundGuide = useMemo(() => DOMPurify.sanitize(product.refundGuide), [product.refundGuide])
  const sanitizedExchangeGuide = useMemo(() => DOMPurify.sanitize(product.exchangeGuide), [product.exchangeGuide])
  const sanitizedOfficeAddressLabel = useMemo(
    () => DOMPurify.sanitize(product.partner?.officeAddressLabel),
    [product.partner?.officeAddressLabel]
  )

  const toggleAccordion = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const tabBtn = event.currentTarget
    const nextTabCont = tabBtn.nextElementSibling as HTMLElement

    const isOpen = tabBtn.classList.contains('open')

    if (!isOpen) {
      tabBtn.classList.add('open')
      nextTabCont.style.display = 'block'
    } else {
      tabBtn.classList.remove('open')
      nextTabCont.style.display = 'none'
    }
  }, [])

  useEffect(() => {
    if (!product || !tabBtnsRef.current) return
    const tabBtns = tabBtnsRef.current.querySelectorAll('li button')
    const handleToggleAccordion = (event: unknown) => toggleAccordion(event as React.MouseEvent<HTMLButtonElement>)
    tabBtns.forEach((btn) => {
      btn.addEventListener('click', handleToggleAccordion)
    })

    return () => {
      tabBtns.forEach((btn) => {
        btn.removeEventListener('click', handleToggleAccordion)
      })
    }
  }, [product])

  return (
    <>
      {/* 상세 이미지 영역 */}

      <ProductDetailImageArea dangerouslySetInnerHTML={{ __html: sanitizedContent }}></ProductDetailImageArea>

      {/* 상품 고시 정보, 배송/환불/안내, 판매자 목록 영역 아코디언 */}
      <ul className='product_detail_infolist' ref={tabBtnsRef}>
        <li>
          <button>상품 고시정보</button>
          <div className='contents'>
            {productInfo?.contents?.map((el: ProductDutyInfoContents, idx: number) => (
              <dl key={idx}>
                <dt>{keyArray[idx - 1]}</dt>
                <dd>{valueArray[idx - 1]}</dd>
              </dl>
            ))}
          </div>
        </li>
        <li>
          <button>배송/환불 안내</button>
          <div className='contents'>
            <div dangerouslySetInnerHTML={{ __html: sanitizedDeliveryGuide }}></div>
            <div dangerouslySetInnerHTML={{ __html: sanitizedRefundGuide }}></div>
            <div dangerouslySetInnerHTML={{ __html: sanitizedExchangeGuide }}></div>
          </div>
        </li>
        <li>
          <button>판매자 정보</button>
          <div className='contents company'>
            <dl>
              <dt>상호명</dt>
              <dd>{product.partner?.companyName}</dd>
            </dl>
            <dl>
              <dt>대표자</dt>
              <dd>{product.partner?.ownerName}</dd>
            </dl>
            <dl>
              <dt>사업자등록번호</dt>
              <dd>{product.partner?.businessRegistrationNo}</dd>
            </dl>
            <dl>
              <dt>사업장 소재지</dt>
              <dd dangerouslySetInnerHTML={{ __html: sanitizedOfficeAddressLabel }}></dd>
            </dl>
            <dl>
              <dt>이메일</dt>
              <dd>{product.partner?.email}</dd>
            </dl>
            <dl>
              <dt>전화번호</dt>
              <dd>{product.partner?.phoneNo.replace(/^(\d{1,2})(\d{2,3})(\d{4})$/, `$1-$2-$3`)}</dd>
            </dl>
          </div>
        </li>
      </ul>
    </>
  )
}

export default memo(ProductDetailContents)

// 상세 이미지 영역
export const ProductDetailImageArea = styled.div`
  width: 100%;
  & img {
    width: 100%;
  }
  & p {
    width: 100%;
  }
`
